import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';

const REPORT_PDF_URL = '/api/samples/report.pdf';
const STATEMENT_HTML_URL = '/api/samples/statement.html';

export default function SamplePage() {
  useSeo({
    title: 'Sample WCAG report + accessibility statement',
    canonicalPath: '/sample',
    description:
      'Download a real EAA Proof PDF report and EN 301 549-shaped accessibility statement HTML — generated by the same code paths that ship every paid scan.',
  });

  return (
    <div className="page sample-preview">
      <header className="sample-hero">
        <span className="hero-badge">
          <span className="hero-badge-dot" aria-hidden="true" />
          Generated by the same code that ships every customer report
        </span>
        <h1>See what a real EAA Proof report looks like</h1>
        <p className="lead">
          Below is a live preview of the two files we deliver after every scan: a developer-ready
          WCAG audit PDF and an EN&nbsp;301&nbsp;549-shaped accessibility statement you can publish
          at <code>/accessibility</code>. We render them on demand using the same code paths that
          handle paid scans — no marketing mockups, no "representative" art.
        </p>
        <div className="sample-actions">
          <a className="cta" href={REPORT_PDF_URL} download="eaa-proof-sample-report.pdf">
            Download sample PDF
          </a>
          <a className="cta secondary" href={STATEMENT_HTML_URL} download="sample-accessibility-statement.html">
            Download sample statement
          </a>
        </div>
      </header>

      <section className="sample-grid" aria-label="Sample artifacts">
        <article className="sample-card">
          <header className="sample-card-head">
            <h2>WCAG audit PDF</h2>
            <p className="muted small">
              Cover page · executive summary · per-violation remediation with before/after code · EAA citations.
            </p>
            <a className="sample-card-action" href={REPORT_PDF_URL} target="_blank" rel="noopener noreferrer">
              Open in new tab ↗
            </a>
          </header>
          <div className="sample-frame sample-frame-pdf">
            <object data={`${REPORT_PDF_URL}#toolbar=0`} type="application/pdf" aria-label="Sample EAA Proof PDF report">
              <p className="muted">
                Your browser can't embed PDFs.{' '}
                <a href={REPORT_PDF_URL} download>Download the sample report</a> to view it.
              </p>
            </object>
          </div>
        </article>

        <article className="sample-card">
          <header className="sample-card-head">
            <h2>Accessibility statement (HTML)</h2>
            <p className="muted small">
              Conformance status · known issues · feedback contact · enforcement procedure pointer. Publish as-is.
            </p>
            <a className="sample-card-action" href={STATEMENT_HTML_URL} target="_blank" rel="noopener noreferrer">
              Open in new tab ↗
            </a>
          </header>
          <div className="sample-frame sample-frame-html">
            <iframe
              src={STATEMENT_HTML_URL}
              title="Sample accessibility statement"
              sandbox="allow-same-origin"
              loading="lazy"
            />
          </div>
        </article>
      </section>

      <section className="sample-facts" aria-labelledby="sample-facts-heading">
        <h2 id="sample-facts-heading">What's actually in the report</h2>
        <ul>
          <li>
            <strong>Compliance score</strong> — 100 minus a weighted penalty (critical −10, serious −5,
            moderate −2, minor −1; floored at 0). The same formula used in your real report.
          </li>
          <li>
            <strong>Plain-English explanation per violation</strong> — produced by Claude Sonnet 4.6
            with five anti-hallucination guards (grounding check, substring verification on code
            examples, etc.). If a guard fires, the raw axe-core data is shown instead of an AI fix —
            never a fabricated one.
          </li>
          <li>
            <strong>Before/after code examples</strong> — pulled from the actual HTML on your page.
            Guaranteed to be a substring of what the scanner observed.
          </li>
          <li>
            <strong>EAA citation per violation</strong> — verbatim quote from Directive 2019/882
            so your lawyer can trace each finding back to a regulatory clause.
          </li>
          <li>
            <strong>EN 301 549 accessibility statement</strong> — conformance level, inaccessible
            content list, preparation method, feedback contact, and enforcement-procedure pointer.
            Drop it on <code>/accessibility</code> as-is.
          </li>
        </ul>
      </section>

      <section className="sample-cta">
        <h2>Ready to scan your own site?</h2>
        <p>
          One scan costs €29 (or €9/mo for 5 scans). You see the full report before you publish anything.
        </p>
        <div className="sample-actions">
          <Link to="/" className="cta">Run a scan</Link>
          <Link to="/pricing" className="cta secondary">See pricing</Link>
        </div>
      </section>
    </div>
  );
}
